// Ownership / selection / card-group logic, extracted from
// GameBoard.jsx. Decides which meshes the local player may interact
// with (seat ownership, freeform-draw gate, spectator lockout), paints
// ownership outlines + selection highlights, and manages the manual
// card groups (assign / dissolve / evict / freeze) that make marquee
// drags move as one unit.

import { getAccessibilitySettings } from '../../../utils/game/accessibilitySettings';
import { createOwnershipOutlineMesh, createTargetHaloMesh } from '../../../utils/game/cardMesh';
import { createContestedCellRing, disposeContestedCellRing } from '../../../utils/game/visualEffects/contestedCellRing';
import { getGameGrid } from '../../../utils/game/spawnConfig';
import { sampleCellGeometry } from '../../../utils/game/engine/core';
import * as hotkeys from '../controllers/hotkeys';

// --- Ownership ---
// In multiplayer, P1 (host) owns rotated=false objects, P2 (guest) owns rotated=true.
// In single-player (offline), everything is owned by the local player.
export function isOwnedByLocalPlayer(host, obj) {
  if (host.props.isSpectating) return false;
  if (host.state.connectionStatus === 'offline') return true;
  const isLocalP2 = !host.state.isHost;
  if (obj?.type === 'card' || obj?.type === 'pile') {
    return !!obj.cardInstance?.rotated === isLocalP2 || !!obj.pile?.rotated === isLocalP2;
  }
  // Tokens and dice have no owner. Anyone can interact
  return true;
}

// True if the card sits on the local player's side of the table.
// Purely geometric: compares the card's `rotated` flag against the
// local player's seat (`isHost` → P1, else P2). This is DIFFERENT
// from `isOwnedByLocalPlayer`, which answers "can the local player
// interact with this card?" The latter short-circuits to true in
// offline sandbox mode so the user can drag every card. Outline
// coloring needs the side-of-table view instead, and must avoid the
// `connectionStatus === 'offline'` shortcut because during a match
// handshake the guest's state is briefly 'offline' before the
// `setState({connectionStatus:'connected'})` from initSession
// flushes. Coloring in that window would lock every card as "own".
export function isCardOnLocalSide(host, cardInstance) {
  if (!cardInstance) return true;
  // Casual sandbox: the single user drives both seats via Tab, so
  // every card on the board is "theirs" from an interaction standpoint
  // (action menu, right-click, combat, drag, activated abilities).
  // Matches `isPileLocallyOwned`'s sandbox bypass. The two helpers
  // gate the same set of interactions and have to agree, or one side
  // of the table goes dead in solo play. This was the root cause of
  // the P2-side Avatar of Fire (and every other avatar's) Activate
  // button greying out in sandbox.
  if (host.props.isCasualSandbox) return true;
  const isLocalP2 = !host.state.isHost;
  return !!cardInstance.rotated === isLocalP2;
}

// Whether the local player may invoke pile-private actions (draw,
// shuffle, search, scry, draw-to-hand) on this pile. Sorcery TCG
// forbids touching the opponent's spellbook/atlas, so in real
// matches these actions are gated to the side of the table that
// matches the local seat. Casual sandbox bypasses the gate so a
// single user driving both seats can test interactions freely.
export function isPileLocallyOwned(host, pile) {
  if (!pile) return true;
  // Spectators don't own anything. Every pile is "opponent" from
  // their POV, which keeps the existing isPileLocallyOwned-gated
  // context-menu / drag / draw paths off for them automatically.
  if (host.props.isSpectating) return false;
  if (host.state.connectionStatus === 'offline') return true;
  if (host.props.isCasualSandbox) return true;
  return !!pile.rotated === !host.state.isHost;
}

// v1.66. Whether the player can freeform-draw from a pile (left-
// click, hotkey, drag-from-pile, context-menu "Draw card" /
// "Draw to hand"). In sandbox + offline, allowed for testing /
// debugging. In real matches, draws must go through the engine
// (Start Phase prompt, card-effect draws, Spellcaster intercept).
// Engine-driven draws bypass this gate by calling drawCard directly.
// v1.104. Draw automation isn't complete; players need manual
// pile control in every match. Always allow freeform draw (hotkeys,
// drag-from-pile, right-click Draw N).
export function canFreeformDraw(host) {
  return true;
}

// Accessibility: paint a colored ring around each card based on its
// controller. The ring is a child mesh inside the card's local
// transform (not an HTML overlay) so zoom and pan affect it exactly
// like the card itself. Called on card spawn and whenever the user
// changes the accessibility settings.
export function applyOwnershipOutline(host, mesh, settings) {
  if (!mesh || mesh.userData?.type !== 'card') return;
  const s = settings || getAccessibilitySettings();
  const existing = mesh.userData.ownershipOutline;
  if (existing) {
    mesh.remove(existing);
    existing.geometry?.dispose();
    existing.material?.dispose();
    mesh.userData.ownershipOutline = null;
  }
  if (!s.ownershipOutlines) return;
  const isOwn = host.isCardOnLocalSide(mesh.userData.cardInstance);
  const color = isOwn ? s.ownOutlineColor : s.opponentOutlineColor;
  const outline = createOwnershipOutlineMesh(s.outlineThickness, color);
  mesh.add(outline);
  mesh.userData.ownershipOutline = outline;
}

export function refreshOwnershipOutlines(host) {
  const settings = getAccessibilitySettings();
  for (const mesh of host.meshes.values()) {
    host.applyOwnershipOutline(mesh, settings);
  }
}

// --- 3D Interaction ---
export function getInteractableMeshes(host) {
  const meshes = [...host.meshes.values(), ...host.pileMeshes.values(), ...host.tokenMeshes.values(), ...host.diceMeshes.values(), ...host.trackerButtonMeshes.values()];
  for (const mesh of host.opponentHandMeshes) {
    if (mesh.userData?.type === 'opponentHandFace') meshes.push(mesh);
  }
  return meshes;
}

/**
 * Compose the set of cards that should visually register as "selected"
 * right now: the marquee selection union'd with every group that has at
 * least one selected member. This is the single source of truth the
 * highlight pass and the multi-drag pickup both read from.
 */
// --- Multi-selection & grouping ---
export function collectHighlightedCardIds(host) {
  const ids = new Set(host.selectedCardIds);
  // If any selected card belongs to a group, pull every other member
  // in. Selecting one member implies selecting the whole group.
  for (const cardId of host.selectedCardIds) {
    const mesh = host.meshes.get(cardId);
    const gid = mesh?.userData?.cardInstance?.groupId;
    if (gid && host.groups.has(gid)) {
      for (const memberId of host.groups.get(gid)) ids.add(memberId);
    }
  }
  return ids;
}

/**
 * Collect the cards that should drag together when `primaryCardId` is
 * picked up: the primary card itself, every other card currently in the
 * same marquee selection, and every card that shares a group with any
 * of those. Returns an ordered Set with the primary card first so
 * callers can compute the drag offset from it.
 */
export function collectDragGroupForCard(host, primaryCardId) {
  const result = new Set([primaryCardId]);

  // If the primary is in the marquee selection, pull every selection
  // member along. If the primary is NOT selected, we still pick up its
  // group so right-clicking (which doesn't select) still works for
  // grouped cards.
  const primaryIsSelected = host.selectedCardIds.has(primaryCardId);
  if (primaryIsSelected) {
    for (const id of host.selectedCardIds) result.add(id);
  }

  // Expand via groups: for every card already in `result`, follow its
  // groupId and add every member.
  const seeds = [...result];
  for (const id of seeds) {
    const mesh = host.meshes.get(id);
    const gid = mesh?.userData?.cardInstance?.groupId;
    if (gid && host.groups.has(gid)) {
      for (const memberId of host.groups.get(gid)) result.add(memberId);
    }
  }

  return result;
}

/**
 * Repaint emissive tints across every card mesh so the selected/grouped
 * cards read clearly. Selected (marquee) cards get a bright gold glow,
 * grouped cards get a softer gold tint, and everything else is cleared.
 * Safe to call frequently. It's a cheap loop over meshes with no
 * allocations beyond a Set.
 */
export function updateSelectionHighlights(host) {
  const highlighted = host.collectHighlightedCardIds();
  // Only cards that are in the active marquee selection get an
  // emissive tint. Grouped (but unselected) cards look identical to
  // ungrouped ones. The hug outline wraps them together when
  // selected, and multi-drag is what surfaces the group at drag
  // time. This keeps the board visually quiet during normal play.
  for (const [cardId, mesh] of host.meshes) {
    if (!mesh?.material) continue;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const emissive = highlighted.has(cardId) ? 0x5a3f0a : 0x000000;
    for (const m of mats) {
      if (m.emissive) m.emissive.setHex(emissive);
    }
  }
  // The hug rect depends on the same highlighted set, so refresh it
  // in lockstep. This gives instant feedback when the marquee adds
  // or removes a card. The hug outline snaps to match.
  host.updateHugRect();
}

/**
 * Freeze all member bodies in a group by making them STATIC.
 * Static bodies are immovable (no gravity, no solver jitter) but
 * other dynamic bodies still collide with them, so a stack sitting
 * on the table blocks anything dropped on top.
 *
 * Inspired by how Tabletop Simulator handles grouped cards: instead
 * of N bodies with fragile constraints, the group is treated as one
 * immovable object until the user explicitly picks it up.
 */
export function freezeGroup(host, groupId) {
  // No-op under the computed-Y architecture. Bodies are kinematic +
  // sensor from creation, so there's no physics state to freeze.
  // Group cohesion now comes from coordinated drag movement and
  // shared interactionOrder bumps; the body type is irrelevant.
  void groupId;
}

export function unfreezeGroup(host, groupId) {
  // No-op. See freezeGroup.
  void groupId;
}

/**
 * Assign a groupId to a list of card ids. Bodies are left DYNAMIC
 * for a brief settling period (so gravity pulls them onto the table),
 * then a delayed freeze switches them to STATIC. No constraints are
 * used. Static bodies can't move, so the group is perfectly rigid.
 */
export function assignGroup(host, groupId, cardIds) {
  const members = new Set();
  const affectedOldGroups = new Set();
  for (const cardId of cardIds) {
    const mesh = host.meshes.get(cardId);
    if (!mesh) continue;
    const instance = mesh.userData.cardInstance;
    if (instance.groupId && instance.groupId !== groupId) {
      const old = host.groups.get(instance.groupId);
      if (old) {
        old.delete(cardId);
        affectedOldGroups.add(instance.groupId);
        if (old.size === 0) host.groups.delete(instance.groupId);
      }
    }
    instance.groupId = groupId;
    members.add(cardId);
  }
  if (members.size === 0) return;
  host.groups.set(groupId, members);

  // Schedule freeze after a settling window so gravity can pull
  // the cards onto the table first.
  host.groupSettleTimes.set(groupId, performance.now());

  // Any old groups that lost members: if they still have 2+ members
  // they stay valid. Otherwise dissolve.
  for (const oldGroupId of affectedOldGroups) {
    const remaining = host.groups.get(oldGroupId);
    if (!remaining || remaining.size < 2) {
      if (remaining) {
        for (const id of remaining) {
          const m = host.meshes.get(id);
          if (m?.userData?.cardInstance) delete m.userData.cardInstance.groupId;
        }
        host.groups.delete(oldGroupId);
        host.groupSettleTimes.delete(oldGroupId);
      }
    }
  }

  host.updateSelectionHighlights();
}

export function dissolveGroup(host, groupId) {
  host.unfreezeGroup(groupId);
  const members = host.groups.get(groupId);
  if (!members) return;
  for (const cardId of members) {
    const mesh = host.meshes.get(cardId);
    if (mesh?.userData?.cardInstance) {
      delete mesh.userData.cardInstance.groupId;
    }
  }
  host.groups.delete(groupId);
  host.groupSettleTimes.delete(groupId);
  host.updateSelectionHighlights();
}

/**
 * Remove a card from whatever group it belongs to. Dissolves the
 * group entirely if it drops below 2 members.
 */
export function evictCardFromGroup(host, cardId) {
  const mesh = host.meshes.get(cardId);
  const gid = mesh?.userData?.cardInstance?.groupId;
  if (!gid) return;
  const members = host.groups.get(gid);
  if (!members) return;
  members.delete(cardId);
  delete mesh.userData.cardInstance.groupId;
  if (members.size < 2) {
    // Singleton. Dissolve the remainder
    for (const remainingId of members) {
      const m = host.meshes.get(remainingId);
      if (m?.userData?.cardInstance) delete m.userData.cardInstance.groupId;
    }
    host.groups.delete(gid);
    host.groupSettleTimes.delete(gid);
  }
}

export function findHitObject(host, hitObject) {
  let obj = hitObject;
  while (obj) {
    if (obj.userData?.type === 'card' || obj.userData?.type === 'pile' || obj.userData?.type === 'token' || obj.userData?.type === 'dice' || obj.userData?.type === 'trackerButton' || obj.userData?.type === 'opponentHandFace') return obj;
    obj = obj.parent;
  }
  return hitObject;
}

/**
 * Bundle every currently-selected card into a new group. The right-
 * clicked card is implicitly part of the group if it wasn't already
 * in the selection (so right-clicking a single card + "Group" still
 * does something sensible. It just groups that card with the rest
 * of the selection). Broadcasts via setGroup so the opponent's
 * client mirrors the grouping.
 */
export function handleGroupSelected(host, clickedCardInstance) {
  const ids = new Set(host.selectedCardIds);
  if (clickedCardInstance?.id) ids.add(clickedCardInstance.id);
  if (ids.size < 2) { host.closeContextMenu(); return; }
  const groupId = `group-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  host.assignGroup(groupId, [...ids]);
  host._broadcastGroup(groupId, [...ids]);
  host.closeContextMenu();
}

/**
 * Dissolve the group the clicked card belongs to. No-op if the card
 * isn't currently grouped.
 */
export function handleUngroup(host, clickedCardInstance) {
  const gid = clickedCardInstance?.groupId;
  if (!gid) { host.closeContextMenu(); return; }
  host.dissolveGroup(gid);
  host._broadcastClearGroup(gid);
  host.closeContextMenu();
}


// ── Pick-target halos (3D) ────────────────────────────────────────
//
// Rounded card-ring meshes mounted as children of the card meshes.
// they trace the card's actual rounded shape, stay glued through
// camera moves, and never overlap neighbours the way the old
// screen-space DOM rectangles did. One sync pass per frame diffs the
// wanted set (cheap: usually empty) and drives the pulse.
//
// Colors mirror the retired CSS halo variants.
const HALO_COLORS = {
  defender: 0x6edcc8, // teal. Eligible defender / interceptor
  chosen:   0xf0c660, // gold, steady. Locked-in combat selection
  caster:   0xa78bfa, // violet. Spellcaster pick
  equip:    0xe8c46e, // gold. Equip target / single-target effect
  pending:  0xf0c660, // gold. Equip SOURCE awaiting its target
};

export function syncTargetHaloMeshes(host) {
  const targetIds = host.getEquipTargetIds();
  const chosen = host._combatSelectChosenDisplay();
  const variant = host._combatSelect
    ? 'defender'
    : host.state.pendingPrompts?.some((p) => p.promptType === 'pick_caster')
      ? 'caster'
      : 'equip';

  const wanted = new Map();
  if (targetIds) {
    for (const id of targetIds) wanted.set(id, chosen?.has(id) ? 'chosen' : variant);
  }
  if (chosen) {
    for (const id of chosen) if (!wanted.has(id)) wanted.set(id, 'chosen');
  }
  // Equip sources keep their own ring while the target pick is open.
  if (host.state.equipPending?.size) {
    for (const id of host.state.equipPending) if (!wanted.has(id)) wanted.set(id, 'pending');
  }

  if (!host.targetHaloMeshes) host.targetHaloMeshes = new Map();
  for (const [id, rec] of host.targetHaloMeshes) {
    if (wanted.get(id) !== rec.variant || !host.meshes.get(id)) {
      rec.mesh.parent?.remove(rec.mesh);
      rec.mesh.geometry?.dispose();
      rec.mesh.material?.dispose();
      host.targetHaloMeshes.delete(id);
    }
  }
  for (const [id, v] of wanted) {
    if (host.targetHaloMeshes.has(id)) continue;
    const cardMesh = host.meshes.get(id);
    if (!cardMesh) continue;
    const halo = createTargetHaloMesh(HALO_COLORS[v]);
    cardMesh.add(halo);
    host.targetHaloMeshes.set(id, { mesh: halo, variant: v });
  }

  // Pulse the searching variants; chosen / pending stay steady so a
  // locked-in pick reads as settled.
  const pulse = 0.55 + 0.4 * (0.5 + 0.5 * Math.sin(performance.now() * 0.005));
  if (host.targetHaloMeshes.size) {
    for (const [, rec] of host.targetHaloMeshes) {
      rec.mesh.material.opacity =
        (rec.variant === 'chosen' || rec.variant === 'pending') ? 0.95 : pulse;
    }
  }

  syncContestedCellRing(host, pulse);
}

// Subtle stroke ring around the attacked square while a defend /
// intercept pick is open. The "look here" anchor for the prompt.
// Stroke only (no centre fill) so the contested units stay unobscured.
function syncContestedCellRing(host, pulse) {
  const cc = host._combatSelect?.contestedCell || null;
  const key = cc ? `${cc.col},${cc.row}` : null;
  const cur = host.contestedCellRing;
  if (cur && cur.key !== key) {
    host.scene?.scene?.remove(cur.mesh);
    disposeContestedCellRing(cur.mesh);
    host.contestedCellRing = null;
  }
  if (!key) return;
  if (!host.contestedCellRing) {
    const grid = getGameGrid(host.state.spawnConfig);
    const cellGeo = grid && sampleCellGeometry(grid, cc.col, cc.row);
    if (!cellGeo || !host.scene?.scene) return;
    const mesh = createContestedCellRing(cellGeo);
    host.scene.scene.add(mesh);
    host.contestedCellRing = { mesh, key };
  }
  // Breathe with the same rhythm as the eligibility halos, but quieter
  // This is a waypoint, not a pick target.
  host.contestedCellRing.mesh.material.opacity = 0.3 + 0.35 * (pulse - 0.55) / 0.4;
}
